<template>
  <hr-form-dialog
    ref="dialog"
    size="sm"
    :title="$t('insights.editChartSettings.textHeaderChartSettings')"
    @hide="onDialogHide"
    @submit="onSubmitForm"
  >
    <div class="row q-col-gutter-md">
      <hr-list-select
        v-model="editChartSettings.theme"
        :label="$t('insights.editChartSettings.selectThemeLabel')"
        :options="themeList"
        hide-bottom-space
        class="col-12"
      />
      <hr-text-field
        v-model="editChartSettings.chartTitle"
        :label="$t('insights.editChartSettings.chartTitle')"
        hide-bottom-space
        class="col-12"
      />
      <hr-form-section-header
        :label="$t('insights.editChartSettings.yAxisTitle')"
        class="col-12"
      />
      <div class="col-12">
        <hr-checkbox
          v-model="editChartSettings.yAxisLabel"
          :label="$t('insights.editChartSettings.customLabel')"
          true-value="custom"
          false-value="auto"
        />
      </div>
      <hr-text-field
        v-if="editChartSettings.yAxisLabel === 'custom'"
        v-model="editChartSettings.yAxisLabelValue"
        :label="$t('insights.editChartSettings.yAxisLabel')"
        :rules="validationRules.label"
        lazy-rules
        hide-bottom-space
        class="col-12"
      />
      <div class="col-12">
        <hr-checkbox
          v-model="editChartSettings.yAxisMin"
          :label="$t('insights.editChartSettings.customMinValueLabel')"
          true-value="custom"
          false-value="auto"
        />
      </div>
      <template v-if="editChartSettings.yAxisMin === 'custom'">
        <hr-text-field
          v-if="axisType === 'duration'"
          v-model="yAxisMinValueFormatted"
          :label="$t('insights.editChartSettings.yAxisMinValue')"
          :rules="validationRules.minValueDuration"
          mask="#:##:##"
          fill-mask="0"
          hide-bottom-space
          class="col-12"
        />
        <hr-text-field
          v-else-if="axisType === 'percentage'"
          v-model.number="editChartSettings.yAxisMinValue"
          type="number"
          :label="$t('insights.editChartSettings.yAxisMinValue')"
          :rules="validationRules.minValuePercent"
          suffix="%"
          min="0"
          max="100"
          hide-bottom-space
          class="col-12"
        />
        <hr-text-field
          v-else
          v-model.number="editChartSettings.yAxisMinValue"
          type="number"
          :label="$t('insights.editChartSettings.yAxisMinValue')"
          :rules="validationRules.minValue"
          :min="0"
          hide-bottom-space
          class="col-12"
        />
      </template>
      <div class="col-12">
        <hr-checkbox
          v-model="editChartSettings.yAxisMax"
          :label="$t('insights.editChartSettings.customMaxValueLabel')"
          true-value="custom"
          false-value="auto"
        />
      </div>
      <template v-if="editChartSettings.yAxisMax === 'custom'">
        <hr-text-field
          v-if="axisType === 'duration'"
          v-model="yAxisMaxValueFormatted"
          :label="$t('insights.editChartSettings.yAxisMaxValue')"
          :rules="validationRules.maxValueDuration"
          mask="#:##:##"
          fill-mask="0"
          hide-bottom-space
          class="col-12"
        />
        <hr-text-field
          v-else-if="axisType === 'percentage'"
          v-model.number="editChartSettings.yAxisMaxValue"
          type="number"
          :label="$t('insights.editChartSettings.yAxisMaxValue')"
          :rules="validationRules.maxValuePercent"
          suffix="%"
          min="1"
          max="100"
          hide-bottom-space
          class="col-12"
        />
        <hr-text-field
          v-else
          v-model.number="editChartSettings.yAxisMaxValue"
          type="number"
          :label="$t('insights.editChartSettings.yAxisMaxValue')"
          :rules="validationRules.maxValue"
          :min="0"
          hide-bottom-space
          class="col-12"
        />
      </template>
      <div class="col-12">
        <hr-checkbox
          v-model="editChartSettings.yDisableGridLines"
          :label="$t('insights.editChartSettings.disableGridLinesLabel')"
        />
      </div>
      <hr-form-section-header
        :label="$t('insights.editChartSettings.xAxisTitle')"
        class="col-12"
      />
      <div class="col-12">
        <hr-checkbox
          v-model="editChartSettings.xAxisLabel"
          :label="$t('insights.editChartSettings.customLabel')"
          true-value="custom"
          false-value="auto"
        />
      </div>
      <hr-text-field
        v-if="editChartSettings.xAxisLabel === 'custom'"
        v-model="editChartSettings.xAxisLabelValue"
        :label="$t('insights.editChartSettings.xAxisLabel')"
        :rules="validationRules.label"
        lazy-rules
        hide-bottom-space
        class="col-12"
      />
      <div class="col-12">
        <hr-checkbox
          v-model="editChartSettings.xDisableGridLines"
          :label="$t('insights.editChartSettings.disableGridLinesLabel')"
        />
      </div>
      <template v-if="isXAxisSession">
        <div class="col-12">
          <hr-checkbox
            v-model="xAxisUseCustomDateFormat"
            :label="$t('insights.editChartSettings.xAxisCustomDateFormatLabel')"
          />
        </div>
        <hr-list-select
          v-if="editChartSettings.xAxisUseCustomDateFormat"
          v-model="editChartSettings.xAxisDateFormat"
          :label="$t('insights.editChartSettings.xAxisDateFormatLabel')"
          :options="dateFormats"
          hide-bottom-space
          class="col-12"
        />
      </template>
    </div>
    <template #actions>
      <hr-dismiss-btn
        :label="$t('common.buttonCancel')"
        @click="hide"
      />
      <hr-confirm-btn
        type="submit"
        :label="$t('common.buttonApply')"
      />
    </template>
  </hr-form-dialog>
</template>
<script>

import { chartThemeList } from 'boot/globals';
import { cloneDeep } from 'lodash';
import {
  HrCheckbox,
  HrConfirmBtn,
  HrDismissBtn,
  HrFormDialog,
  HrFormSectionHeader,
  HrListSelect,
  HrTextField,
} from 'src/design-system';
import Insights from 'src/mixins/Insights';
import Toolbox from 'src/mixins/Toolbox';

export default {
  name: 'EditChartSettings',

  components: {
    HrCheckbox,
    HrConfirmBtn,
    HrDismissBtn,
    HrFormDialog,
    HrFormSectionHeader,
    HrListSelect,
    HrTextField,
  },

  mixins: [Insights, Toolbox],

  props: {
    chartSettings: {
      type: Object,
      required: true,
    },
    dataSeriesSettings: {
      type: Object,
      required: true,
    },
  },

  emits: ['hide', 'ok'],

  data() {
    return {
      editChartSettings: {
        theme: 'default',
        xAxisLabel: 'auto',
        xAxisLabelValue: '',
        xDisableGridLines: false,
        yAxisLabel: 'auto',
        yAxisLabelValue: '',
        yAxisMin: 'auto',
        yAxisMinValue: 0,
        yAxisMax: 'auto',
        yAxisMaxValue: 0,
        yDisableGridLines: false,
      },
      defaultChartSettings: {
        theme: 'default',
        xAxisLabel: 'auto',
        xDisableGridLines: false,
        yAxisLabel: 'auto',
        yAxisMin: 'auto',
        yAxisMax: 'auto',
        yDisableGridLines: false,
      },

      validationRules: {
        label: [(val) => this.valueValidation(val) || this.$t('insights.editChartSettings.inputCustomLabelValidator')],
        minValuePercent: [
          (val) => (parseInt(val, 10) >= 0) || this.$t('insights.editChartSettings.inputValidatorMoreEq0Pct'),
          (val) => (parseInt(val, 10) < 100) || this.$t('insights.editChartSettings.inputValidatorLess100Pct'),
          (val) => (
            this.editChartSettings.yAxisMax === 'auto' || parseInt(val, 10) < this.editChartSettings.yAxisMaxValue
          ) || this.$t('insights.editChartSettings.inputValidatorMaxMoreMin'),
        ],
        maxValuePercent: [
          (val) => (parseInt(val, 10) > 0) || this.$t('insights.editChartSettings.inputValidatorMore0Pct'),
          (val) => (parseInt(val, 10) <= 100) || this.$t('insights.editChartSettings.inputValidatorLessEq100Pct'),
          (val) => (
            this.editChartSettings.yAxisMin === 'auto' || parseInt(val, 10) > this.editChartSettings.yAxisMinValue
          ) || this.$t('insights.editChartSettings.inputValidatorMaxMoreMin'),
        ],
        minValueDuration: [
          (val) => (
            this.editChartSettings.yAxisMax === 'auto'
            || this.stringToDuration(val) < this.editChartSettings.yAxisMaxValue
          ) || this.$t('insights.editChartSettings.inputValidatorMaxMoreMin'),
        ],
        maxValueDuration: [
          (val) => (
            this.editChartSettings.yAxisMin === 'auto'
            || this.stringToDuration(val) > this.editChartSettings.yAxisMinValue
          ) || this.$t('insights.editChartSettings.inputValidatorMaxMoreMin'),
        ],
        minValue: [
          (val) => (
            this.editChartSettings.yAxisMax === 'auto' || parseInt(val, 10) < this.editChartSettings.yAxisMaxValue
          ) || this.$t('insights.editChartSettings.inputValidatorMaxMoreMin'),
        ],
        maxValue: [
          (val) => (
            this.editChartSettings.yAxisMin === 'auto' || parseInt(val, 10) > this.editChartSettings.yAxisMinValue
          ) || this.$t('insights.editChartSettings.inputValidatorMaxMoreMin'),
        ],
      },
    };
  },

  computed: {

    isXAxisSession() {
      return ['xaTrialCumulative', 'xaTrialBySession', 'xaSession'].includes(this.dataSeriesSettings.xAxis);
    }, // isXAxisSession()

    axisType() {
      return this.dataSeriesSettings?.axis ?? 'number';
    },

    yAxisMinValueFormatted: {
      get() {
        return this.formatDuration(this.editChartSettings.yAxisMinValue || 0);
      },
      set(newValue) {
        this.editChartSettings.yAxisMinValue = this.stringToDuration(newValue);
      },
    }, // yAxisMinValueFormatted()

    yAxisMaxValueFormatted: {
      get() {
        return this.formatDuration(this.editChartSettings.yAxisMaxValue || 0);
      },
      set(newValue) {
        this.editChartSettings.yAxisMaxValue = this.stringToDuration(newValue);
      },
    }, // yAxisMaxValueFormatted()

    xAxisUseCustomDateFormat: {
      get() {
        return this.editChartSettings.xAxisUseCustomDateFormat ?? false;
      },
      set(newValue) {
        this.editChartSettings.xAxisUseCustomDateFormat = newValue;
        if (!this.editChartSettings.xAxisDateFormat) {
          this.editChartSettings.xAxisDateFormat = this.dateFormats[0].value;
        }
      },

    }, // xAxisUseCustomDateFormat()

    themeList() {
      return chartThemeList;
    },
  },

  watch: {
    chartSettings: {
      immediate: true,
      deep: true,
      handler() {
        this.editChartSettings = {
          ...this.defaultChartSettings,
          ...this.chartSettings,
        };
      },
    },
  },

  methods: {
    // following method is REQUIRED
    // (don't change its name --> "show")
    show() {
      this.$refs.dialog.show();
    },

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    },

    async onSubmitForm() {
      this.$emit('ok', cloneDeep(this.editChartSettings));
      this.hide();
    }, // onSubmitForm()

    valueValidation(val) {
      return val ? !!val.trim() : false;
    }, // valueValidation()

  },
};
</script>
